'use client';

import { useEffect, useState } from 'react';
import { useNinetailed } from '@ninetailed/experience.js-next';
import { getColorway } from 'utils/styles';
import { ResolvedVisualSettings } from 'utils/pageHelpers/visualSettings';
import { PageProps } from 'libs/contentful/queries/page/props';
import { PageVisualSettingsProps } from 'libs/contentful/props';
import { Colorways } from '@/types/constants';

interface ClientVisualSettingsUpdaterProps {
  page: PageProps;
  initialSettings: ResolvedVisualSettings;
}

/**
 * Client-side component that updates page visual settings when Ninetailed variants change
 * This is only used in client-side mode (draft preview)
 *
 * The component:
 * 1. Waits for the Ninetailed preview plugin to be ready
 * 2. Listens to Ninetailed profile changes via the preview plugin
 * 3. Finds the selected variant for visual settings
 * 4. Updates the page wrapper classes dynamically
 *
 * Note: This will cause a brief visual flash (FOUC) when variants change,
 * which is acceptable in draft preview mode.
 */
export function ClientVisualSettingsUpdater({ page, initialSettings }: ClientVisualSettingsUpdaterProps) {
  const [isReady, setIsReady] = useState(false);
  const ninetailed = useNinetailed();

  useEffect(() => {
    // Small delay to ensure preview plugin is ready (same as ClientNinetailedExperience)
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Only proceed if plugin is ready
    if (!isReady) {
      return;
    }

    // Only proceed if we have Ninetailed visual settings with experiments
    if (!page.pageVisualSettings?.sys?.id || !page.ninetailedData || !initialSettings.useClientSelection) {
      return;
    }

    const ninetailedId = page.pageVisualSettings.sys.id;
    const ninetailedData = page.ninetailedData.get(ninetailedId);

    if (!ninetailedData?.mappedExperiences) {
      return;
    }

    if (!ninetailed?.onSelectVariant) {
      return;
    }

    const applyVisualSettingsFromVariant = () => {
      const windowNinetailed = (window as unknown as Record<string, unknown>).ninetailed as
        | Record<string, unknown>
        | undefined;

      // Get active variant from Ninetailed's preview plugin
      let previewOverrides: Record<string, number> | null = null;

      if (windowNinetailed) {
        const plugins = windowNinetailed.plugins as Record<string, unknown> | undefined;
        const preview = plugins?.preview as Record<string, unknown> | undefined;
        previewOverrides = (preview?.experienceVariantIndexes as Record<string, number>) || null;
      }

      // If no preview overrides exist yet, check if ANY experience contains our visual settings
      // This handles the initial state before any variant is selected in the preview plugin
      let experience;

      if (previewOverrides && Object.keys(previewOverrides).length > 0) {
        // Find the experience that has a preview override
        experience = ninetailedData.mappedExperiences.find(exp => {
          return previewOverrides && previewOverrides[exp.id] !== undefined;
        });
      } else {
        // No preview overrides yet - check if we have any experience with our visual settings baseline
        // In this case, we'll use the first experience that contains our component
        experience = ninetailedData.mappedExperiences.find(exp => {
          return exp.components?.some(comp => 'id' in comp.baseline && comp.baseline.id === ninetailedId);
        });
      }

      if (!experience) {
        // No experience found at all, use baseline settings from page
        updatePageVisualSettings(initialSettings);
        return;
      }

      // Get the variant index - if no override exists, default to 0 (baseline)
      const variantIndex = (previewOverrides && previewOverrides[experience.id]) ?? 0;

      // Find the component (should be the SectionPageVisualSettings)
      const component = experience.components.find(comp => 'id' in comp.baseline && comp.baseline.id === ninetailedId);

      if (!component) {
        updatePageVisualSettings(initialSettings);
        return;
      }

      // For variantIndex 0, use baseline; otherwise use variants array
      let variantData: unknown;
      if (variantIndex === 0) {
        // Baseline - check if we can use the already-fetched page.pageVisualSettings.pageVisualSettings
        // or if we need the component baseline
        if (page.pageVisualSettings?.pageVisualSettings) {
          // Use the already-fetched pageVisualSettings from the page query
          variantData = { pageVisualSettings: page.pageVisualSettings.pageVisualSettings };
        } else {
          // Fall back to component baseline
          variantData = component.baseline;
        }
      } else {
        // Variant - map variantIndex to variants array (1 = variants[0], 2 = variants[1], etc.)
        const actualVariantIndex = variantIndex - 1;
        if (actualVariantIndex < component.variants.length) {
          variantData = component.variants[actualVariantIndex];
        } else {
          // Index out of bounds, use baseline
          variantData = component.baseline;
        }
      }

      // Extract visual settings from the variant data
      if (variantData && typeof variantData === 'object' && 'pageVisualSettings' in variantData) {
        const variantSettings = (variantData as { pageVisualSettings?: PageVisualSettingsProps }).pageVisualSettings;

        if (variantSettings) {
          const settingsToApply = {
            pageColor: variantSettings.pageColor ?? page.pageColor,
            footerColor: variantSettings.footerColor ?? page.footerColor,
            showFooterKeyline: variantSettings.showFooterKeyline ?? page.showFooterKeyline,
          };

          updatePageVisualSettings(settingsToApply);
        }
      } else {
        // No visual settings in variant, use baseline
        updatePageVisualSettings(initialSettings);
      }
    };

    // Initial application
    applyVisualSettingsFromVariant();

    // Subscribe to variant changes using Ninetailed's onSelectVariant API
    const unsubscribe = ninetailed.onSelectVariant(
      { baseline: { id: ninetailedId }, experiences: ninetailedData.mappedExperiences } as unknown as Parameters<
        typeof ninetailed.onSelectVariant
      >[0],
      () => {
        // When variant changes, detect and apply the new settings
        applyVisualSettingsFromVariant();
      },
    );

    // Cleanup subscription on unmount
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [page, initialSettings, ninetailed, isReady]);

  // This component doesn't render anything
  return null;
}

/**
 * Swaps the colorway classes on an element
 * @param element - The element to swap the colorway classes on
 * @param colorway - The colorway to swap the classes for
 */
function swapColorwayClasses(element: HTMLElement, colorway: Colorways | undefined) {
  const existingClasses = Array.from(element.classList).filter(cls => cls.endsWith('-colorway'));
  existingClasses.forEach(cls => element.classList.remove(cls));

  if (colorway) {
    element.classList.add(getColorway(colorway));
  }
}

/**
 * Updates the page visual settings by manipulating the DOM
 * This is necessary because we can't re-render the entire page wrapper from a client component
 */
function updatePageVisualSettings(settings: Omit<ResolvedVisualSettings, 'useClientSelection' | 'ninetailedId'>) {
  const pageWrapper = document.querySelector('[data-page-wrapper]');
  const footer = document.querySelector('footer[data-footer]');
  const disclosureBanner = document.querySelector('[data-disclosure-banner]');

  if (pageWrapper) {
    swapColorwayClasses(pageWrapper as HTMLElement, settings.pageColor);
  }

  if (disclosureBanner) {
    swapColorwayClasses(disclosureBanner as HTMLElement, settings.pageColor);
  }

  if (footer) {
    swapColorwayClasses(footer as HTMLElement, settings.footerColor);

    // Handle footer keyline visibility
    const keyline = footer.querySelector('.keyline');
    const footerSection = footer.querySelector('.footer-section');

    if (keyline) {
      if (settings.showFooterKeyline) {
        keyline.classList.remove('hidden');
        footerSection?.classList.add('pt-6', 'md:pt-12');
      } else {
        keyline.classList.add('hidden');
        footerSection?.classList.remove('pt-6', 'md:pt-12');
      }
    }
  }
}
